import { useState, useEffect, useCallback } from "react";
import { Bell, BellRinging, Check, CheckCircle, WarningCircle, Info, Lightning, X, Eye } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_CONFIG = {
  info: { icon: Info, color: "#2563eb", bg: "rgba(37,99,235,0.08)" },
  success: { icon: CheckCircle, color: "var(--success)", bg: "rgba(62,102,86,0.08)" },
  warning: { icon: WarningCircle, color: "var(--warning)", bg: "rgba(232,163,89,0.08)" },
  alert: { icon: Lightning, color: "var(--error)", bg: "rgba(184,74,74,0.08)" },
};

function getToken() { return localStorage.getItem("blumove_token"); }

function NotificationItem({ notif, onMarkRead }) {
  const config = TYPE_CONFIG[notif.notification_type] || TYPE_CONFIG.info;
  const Icon = config.icon;
  const isRead = notif.is_read;

  return (
    <div
      data-testid={`notification-${notif.id}`}
      className="flex gap-3 card-hover"
      style={{
        padding: "14px 16px", borderRadius: 12,
        background: isRead ? "transparent" : config.bg,
        border: `1px solid ${isRead ? "var(--border-color)" : "transparent"}`,
        opacity: isRead ? 0.7 : 1,
        cursor: "pointer",
        transition: "all 0.2s",
      }}
      onClick={() => !isRead && onMarkRead(notif.id)}
    >
      <div style={{ width: 36, height: 36, borderRadius: 10, background: config.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} weight="duotone" style={{ color: config.color }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="flex items-center justify-between gap-2">
          <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.85rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{notif.title}</h4>
          {!isRead && <div style={{ width: 8, height: 8, borderRadius: "50%", background: config.color, flexShrink: 0 }} />}
        </div>
        <p style={{ color: "var(--text-muted)", fontSize: "0.78rem", lineHeight: 1.4, marginTop: 2 }}>{notif.message}</p>
        <div className="flex items-center gap-2 mt-1">
          <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", fontFamily: "JetBrains Mono" }}>
            {new Date(notif.created_at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
          </span>
          {notif.target_role && (
            <span style={{ fontSize: "0.6rem", padding: "1px 6px", borderRadius: 9999, background: "var(--bg)", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase" }}>
              {notif.target_role}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Bell icon with badge for navigation
export function NotificationBell({ onClick }) {
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchCount = useCallback(async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/notifications/unread-count`, { headers });
      setUnreadCount(res.data.unread_count);
    } catch {}
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  return (
    <button
      data-testid="notification-bell"
      onClick={onClick}
      style={{ position: "relative", background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
      onMouseEnter={e => e.currentTarget.style.background = "rgba(45,74,62,0.06)"}
      onMouseLeave={e => e.currentTarget.style.background = "none"}
    >
      {unreadCount > 0 ? <BellRinging size={20} weight="duotone" style={{ color: "var(--primary)" }} /> : <Bell size={20} weight="regular" style={{ color: "var(--text-muted)" }} />}
      {unreadCount > 0 && (
        <span style={{
          position: "absolute", top: 0, right: 0,
          width: 16, height: 16, borderRadius: "50%",
          background: "var(--error)", color: "white",
          fontSize: "0.6rem", fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "2px solid var(--bg)",
        }}>
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
}

// Full notification panel
export default function NotificationPanel({ isOpen, onClose }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/notifications/`, { headers });
      setNotifications(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  const markRead = async (notifId) => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/notifications/${notifId}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
    } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/notifications/read-all`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) { console.error(e); }
  };

  if (!isOpen) return null;

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="notification-backdrop"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.1)", zIndex: 99 }}
      />
      {/* Panel */}
      <div
        data-testid="notification-panel"
        className="animate-slide-in-left"
        style={{
          position: "fixed", top: 60, right: 16, width: 400, maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 80px)",
          background: "var(--surface)", border: "1px solid var(--border-color)",
          borderRadius: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
          zIndex: 100, display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: "1px solid var(--border-color)" }}>
          <div className="flex items-center gap-2">
            <Bell size={18} weight="duotone" style={{ color: "var(--primary)" }} />
            <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem" }}>Notificacoes</h3>
            {unreadCount > 0 && (
              <span style={{ padding: "2px 8px", borderRadius: 9999, background: "var(--primary)", color: "white", fontSize: "0.65rem", fontWeight: 700 }}>{unreadCount}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button data-testid="mark-all-read-btn" onClick={markAllRead} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 6, fontSize: "0.75rem", fontWeight: 600, color: "var(--primary)" }}>
                <Eye size={14} style={{ display: "inline", marginRight: 4 }} />Marcar todas
              </button>
            )}
            <button data-testid="close-notifications-btn" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "var(--text-muted)" }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Notifications list */}
        <div style={{ flex: 1, overflowY: "auto", padding: 8 }}>
          {loading ? (
            <p className="text-center p-4" style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>A carregar...</p>
          ) : notifications.length === 0 ? (
            <div className="text-center p-8">
              <Bell size={32} weight="duotone" style={{ color: "var(--border-color)", margin: "0 auto 8px" }} />
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Sem notificacoes</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {notifications.map(n => (
                <NotificationItem key={n.id} notif={n} onMarkRead={markRead} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
